import { Box, Stack } from "@mui/material";
import { I18N_NAMESPACES } from "@src/appV2/i18n/constants";
import { useTu } from "@src/appV2/i18n/useTu";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
import { useModalState } from "@src/lib/ui-react";
import { useEffect } from "react";

import { LogoutConfirmationDialog } from "./LogoutConfirmationDialog";
import { useNavigationTranslationLabels } from "./Navigation/navigationMenu/translationLabels";

const underConstructionImage = `${import.meta.env.BASE_URL}assets/under-construction.png`;

/**
 * Dead-end landing page for Home Health workplace users who hit the app on
 * mobile (native Capacitor app or narrow viewport). HH workplace surfaces
 * (Cases, Visit Verification, Coverage Map) are not designed for mobile yet,
 * so we block all in-app navigation and present a single Logout action.
 *
 * Rendered directly by `NavigationMenuContainer` outside the app layout
 * chrome so no sidebar/nav drawer is reachable.
 */
export function HomeHealthMobileUnsupportedPage() {
  const logoutDialog = useModalState();
  const { tu } = useTu(I18N_NAMESPACES.navigation);
  const translationLabels = useNavigationTranslationLabels();

  useEffect(() => {
    logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_MOBILE_UNSUPPORTED_VIEWED);
  }, []);

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background?.default,
        paddingX: 5,
        paddingTop: "max(24px, var(--safe-area-inset-top))",
        paddingBottom: "max(20px, var(--safe-area-inset-bottom))",
      })}
    >
      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <ClipboardLogo height={30} width={100} />
      </Box>

      <Stack
        spacing={3}
        sx={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          maxWidth: 480,
          marginX: "auto",
        }}
      >
        <Box
          component="img"
          src={underConstructionImage}
          alt=""
          sx={{ width: "100%", maxWidth: 150, height: "auto" }}
        />
        <Title component="h5" variant="h5">
          {tu("Under construction")}
        </Title>
        <Text variant="h6" sx={(theme) => ({ color: theme.palette.text?.secondary })}>
          {tu(
            "We're currently making improvements to our mobile app. When it's ready, we'll let you know!"
          )}
        </Text>
      </Stack>

      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <Button variant="outlined" onClick={logoutDialog.openModal}>
          {translationLabels.logOut}
        </Button>
      </Box>

      <LogoutConfirmationDialog modalState={logoutDialog} />
    </Box>
  );
}
